/**
 * DL RT（完了画面のバナー）でキャリコネクション行を出すかどうか。
 * 判定の本体とキャッシュは useCareerconnectionEligibility。
 */
import { buildAutoRtDialogInfo } from '@/plugins/rt-dialog-migration'
import {
  ensureEligibility,
  peekEligibility,
  type CcVmLike
} from '@/composables/useCareerconnectionEligibility'
import { currentSnapshotFingerprint } from '@/composables/useCareerconnectionSnapshotSource'
import {
  buildMediaPartnerTerms,
  mediaToDialogFields,
  rowRefFromCondition
} from '@/utils/realtime-transfer/careerconnectionSnapshot'

type UnknownRecord = Record<string, any>
type AutoRtArgs = [unknown, UnknownRecord, string, unknown, unknown]

/**
 * DL RT（auto_rt）用: キャリコネクション行を先方判定で出し分ける。
 * eligible → 媒体ロゴ・媒体規約リンク（トークン記法）を merge したバナー表示情報
 * それ以外（未解決・失効・非対象・再判定中）→ null。次の行（他社）のバナーを出し、他社の送客を止めない。
 * ①は毎回 ensure する（同じ指紋なら何もしない。編集されていれば再判定し、解決時に AUTO_RT を再 emit）
 */
export function resolveCareerconnectionAutoRtInfo(
  vm: CcVmLike,
  row: UnknownRecord,
  autoRtArgs: unknown[]
): UnknownRecord | null {
  const ref = rowRefFromCondition(row)
  const [, basicData, formatId, formatData, otherFormatData] =
    autoRtArgs as AutoRtArgs
  const ctx = {
    formatId: String(formatId || ''),
    basicData: (basicData || {}) as UnknownRecord,
    formatData,
    otherFormatData,
    dialogName: 'auto_rt',
    isMypage: vm.isMypage === true,
    autoRtArgs
  }
  ensureEligibility(vm, ref, ctx)
  // 今の画面の値で判定した結果だけを使う（再判定中に古い媒体のバナーを出さない）
  const fingerprint = currentSnapshotFingerprint(ctx)
  if (fingerprint == null) return null
  const entry = peekEligibility(vm, ref, fingerprint)
  if (!entry || entry.status !== 'eligible' || !entry.media) return null
  const info = buildAutoRtDialogInfo(row)
  return {
    ...info,
    // 送信直前の再判定が同じキャッシュ（cond:<id>）を引けるように条件行 ID を持たせる
    conditionId: ref.conditionId,
    ...mediaToDialogFields(entry.media),
    ...buildMediaPartnerTerms(entry.media, info.clientServiceName)
  }
}

/** ①の結果がまだ出ていない（未実行・実行中・今の画面の値で未判定）か。バナーの待ち判定に使う */
export function isCareerconnectionAutoRtPending(
  vm: CcVmLike,
  row: UnknownRecord,
  autoRtArgs: unknown[]
): boolean {
  const [, basicData, formatId, formatData, otherFormatData] =
    autoRtArgs as AutoRtArgs
  const fingerprint = currentSnapshotFingerprint({
    formatId: String(formatId || ''),
    basicData: (basicData || {}) as UnknownRecord,
    formatData,
    otherFormatData
  })
  if (fingerprint == null) return true
  const entry = peekEligibility(vm, rowRefFromCondition(row), fingerprint)
  return !entry || entry.status === 'pending'
}
